import { CONNECTION_CATEGORY_FILE } from 'js/constants/connectionCategories';
import { filter, forEach, mapValues } from 'lodash';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import {
  DataFeedSpec,
  FeedSetting,
  SelectedFeedsType,
  VendorFeedSpecs,
} from 'new/components/flows/types/elt/CreateELTFlow/types';
import flowTypes from 'new/components/flows/types/flowTypes';
import { getCurrentParameterValues } from '../getVendorFeedSpecs';
import { createVendorFeedSpecsWrapper } from '../utils/vendorFeedSpecsUtils';
import { getDebugLogger } from 'new/hooks/useDebugLogger';

// Debug logging is now controlled by the UI_ENABLE_DEBUGGER feature flag
const debugLogger = getDebugLogger();

export function createResourceSetupService(flowType = flowTypes.elt) {
  /**
   * Merges UI feed changes with existing backend feed data
   * This ensures we don't lose backend properties that aren't managed by the UI
   * AND preserves the exact property order from the backend for audit logs
   *
   * Strategy: Deep merge with order preservation
   * 1. Start with backend structure (preserves order)
   * 2. Deep merge UI changes (preserves nested structure order)
   * 3. Handle future backend properties gracefully during UI lag
   */
  function mergeFeedWithBackendData(uiFeedData: any, existingBackendFeed: any): any {
    if (!existingBackendFeed) {
      return uiFeedData;
    }

    // Start with the backend structure to preserve property order for audit logs
    const mergedFeed = { ...existingBackendFeed };

    // Deep merge UI changes while preserving backend order
    if (uiFeedData.spec && existingBackendFeed.spec) {
      // Preserve backend spec order, overlay UI changes
      mergedFeed.spec = deepMergePreservingOrder(existingBackendFeed.spec, uiFeedData.spec);
    } else if (uiFeedData.spec) {
      // No existing spec, use UI spec
      mergedFeed.spec = uiFeedData.spec;
    }

    // Update other UI-managed properties (preserving backend order)
    if (uiFeedData.type) mergedFeed.type = uiFeedData.type;

    // The UI data should now override any conflicting backend properties
    // while preserving the order and any unknown backend properties

    return mergedFeed;
  }

  /**
   * Deep merge that preserves the order of the base object
   * This is crucial for audit logs to minimize diff size
   */
  function deepMergePreservingOrder(base: any, overlay: any): any {
    if (!base || typeof base !== 'object') return overlay;
    if (!overlay || typeof overlay !== 'object') return base;

    // Start with base to preserve order
    const result = { ...base };

    // Overlay changes while preserving base order
    Object.keys(overlay).forEach((key) => {
      if (overlay[key] !== undefined) {
        if (typeof overlay[key] === 'object' && !Array.isArray(overlay[key])) {
          // Recursive merge for nested objects
          result[key] = deepMergePreservingOrder(base[key], overlay[key]);
        } else {
          // Direct override for primitives and arrays
          result[key] = overlay[key];
        }
      }
    });

    return result;
  }

  function canEditPrefix({ connectionCategory, selectedBucket, sinkConfig }) {
    return (
      connectionCategory === CONNECTION_CATEGORY_FILE &&
      selectedBucket &&
      selectedBucket?.path &&
      sinkConfig?.['ui.selected_file_mime_type'] !== 'application/vnd.google-apps.spreadsheet'
    );
  }

  function getBucketInfoFromConfig(config) {
    if (!config) return {};
    const { bucket, prefix, path, 'ui.display_path': displayPath } = config;
    let effectivePath;
    let selectedBucket;

    if (path) {
      effectivePath = path;
    } else if (bucket) {
      effectivePath = bucket;

      if (prefix) {
        const separator = effectivePath.endsWith('/') || prefix.startsWith('/') ? '' : '/';
        effectivePath += `${separator}${prefix}`;
      }
    }

    if (effectivePath) {
      const id = effectivePath;
      const lastSlashIndex = id.lastIndexOf('/');
      const name = id.substr(lastSlashIndex + 1);
      const parent = lastSlashIndex > -1 ? id.substr(0, lastSlashIndex) : null;
      const numSlashes = (id.match(/\//g) || []).length;
      const level = numSlashes + 1;

      selectedBucket = {
        id,
        path: effectivePath,
        displayPath: displayPath || effectivePath,
        name,
        parent,
        scanned: false,
        open: false,
        error: null,
        level,
        children: [],
      };
    }

    return {
      selectedBucket,
      effectivePath,
      displayPath,
    };
  }

  function getMissingFields(sourceConfigFields, sourceConfig, templateVersionInfo = null) {
    // For v2 templates: opt-in approach - only validate fields explicitly marked as is_common_param: true
    // For v1 templates: opt-out approach - validate all fields except those marked as is_common_param: false
    const isV2Template = templateVersionInfo?.isV2Template || false;

    let relevantCommonFields;
    if (isV2Template) {
      // V2: Only validate fields explicitly marked as is_common_param: true
      relevantCommonFields = filter(
        sourceConfigFields,
        ({ is_common_param }) => is_common_param === true,
      );
    } else {
      // V1: Validate all fields except those marked as is_common_param: false (default behavior)
      relevantCommonFields = filter(
        sourceConfigFields,
        ({ is_common_param = true }) => is_common_param,
      );
    }

    return getEmptyRequiredFields(relevantCommonFields, sourceConfig);
  }

  function buildSourceInfo({
    sourceConfig,
    selectedFeeds,
    name,
    description,
    originalFeeds = {},
    availableFeeds = [],
    vendorFeedSpecs = null,
    templateVersionInfo = null,
  }): any {
    if (!sourceConfig) {
      return {
        name,
        description: description || '',
        template_config: {
          params: {},
          feeds_config: { feeds: {} },
        },
      };
    }
    // Extract structured data from sourceConfig
    const { vendor_endpoint_id, flow_type = flowType, ...params } = sourceConfig;

    // Get all available feeds, not just selected ones
    // const allFeeds = availableFeeds.map((feed) => feed.value);

    // Create wrapper for type-safe access to vendor feed specs
    const vendorFeedSpecsWrapper = vendorFeedSpecs
      ? createVendorFeedSpecsWrapper(vendorFeedSpecs, templateVersionInfo?.templateVersion || 'v1')
      : null;

    debugLogger.debugLog(1, '=== BUILD SOURCE INFO DEBUG ===');
    debugLogger.debugLog(1, 'Source config:', sourceConfig);
    debugLogger.debugLog(1, 'Vendor feed specs available:', !!vendorFeedSpecs);
    debugLogger.debugLog(1, 'Params:', params);
    debugLogger.debugLog(
      1,
      'Object detection mode selector param:',
      vendorFeedSpecsWrapper?.getObjectDetectionModeParam(),
    );
    debugLogger.debugLog(1, 'Selected feeds:', selectedFeeds);
    debugLogger.debugLog(
      1,
      'Available feeds:',
      availableFeeds.map((feed) => feed.value),
    );
    debugLogger.debugLog(1, 'Original feeds:', originalFeeds);
    debugLogger.debugLog(1, 'Template version info:', templateVersionInfo);

    // Use the helper function to get current parameter values
    const { objectDetectionModeEnabled, singleSchemaValue } = getCurrentParameterValues(
      sourceConfig,
      vendorFeedSpecs,
      templateVersionInfo?.templateVersion,
    );

    // Debug: Log what we're working with
    debugLogger.debugLog(2, '=== PARAMETER VALUES DEBUG ===');
    debugLogger.debugLog(2, 'Object detection mode enabled:', objectDetectionModeEnabled);
    debugLogger.debugLog(2, 'Single schema value:', singleSchemaValue);
    debugLogger.debugLog(2, 'Source config params:', sourceConfig);
    debugLogger.debugLog(2, 'Vendor feed specs:', vendorFeedSpecs);
    debugLogger.debugLog(
      2,
      'Object detection mode selector param name:',
      vendorFeedSpecsWrapper?.getObjectDetectionModeParam(),
    );
    debugLogger.debugLog(2, '=====================================');

    // Process feeds based on selection state
    const processedFeeds: Record<string, any> = {};
    let feeds: Record<string, any> = {};

    // Process ALL available feeds (not just selected ones)
    availableFeeds.forEach(({ value: feed }) => {
      const feedSelection = selectedFeeds[feed];
      const originalFeed = originalFeeds[feed];

      // If no feed selection exists, create a default unselected state
      if (!feedSelection) {
        // For V2 templates, use the feed's default ref_spec from vendorFeedSpecs
        let defaultRefSpec = 'base_auto'; // Default fallback
        let defaultPrimaryKeys = [];
        let feedParams: any = {};
        let refSpecResourceName = 'feed_spec.object_name';

        if (vendorFeedSpecsWrapper?.isV2Template()) {
          const feedOptions = vendorFeedSpecs?.data_feed_options?.[feed];
          if (feedOptions) {
            defaultRefSpec = feedOptions.default_ref || 'base_auto';
            defaultPrimaryKeys = feedOptions.primary_keys || [];

            // Get the base feed params from data_feed_options for unselected feeds too
            feedParams = { ...feedOptions.feed_params };

            // Set the resource name parameter
            if (feedParams?.resource_name_param) {
              refSpecResourceName = feedParams.resource_name_param;
            }
          }
        }

        processedFeeds[feed] = {
          type: 'data',
          spec: {
            ref_spec: defaultRefSpec,
            'primary.keys': defaultPrimaryKeys,
            params: {
              [refSpecResourceName]: feed,
              'ui.feed_display_name': feed,
              // Include all feed-specific parameters for V2 templates (even for unselected feeds)
              ...(vendorFeedSpecsWrapper?.isV2Template() ? feedParams : {}),
            },
            skip_feed: true, // Mark as skipped by default
          },
        };
        return;
      }

      // Build feed spec for selected feeds
      const shouldAddFieldInfo =
        feedSelection?.ref_field_list_selector && feedSelection?.ref_fields_names;
      const useFieldsList =
        feedSelection?.selectedFields &&
        Object.keys(feedSelection?.selectedFields || {}).length > 0;

      let primaryKeys = [];
      if (feedSelection?.feed_selected && feedSelection?.primary_keys) {
        // Handle both string (V1) and array (V2) formats
        if (Array.isArray(feedSelection.primary_keys)) {
          primaryKeys = feedSelection.primary_keys;
        } else {
          primaryKeys = feedSelection?.primary_keys?.split(',') || [];
        }
      } else {
        // For unselected feeds, get default primary keys from vendor feed specs
        if (vendorFeedSpecsWrapper) {
          const defaultPrimaryKey = vendorFeedSpecsWrapper.getPrimaryKey(feed);
          primaryKeys = [defaultPrimaryKey];
        } else {
          primaryKeys = ['']; // Default fallback
        }
      }

      // Get ref_spec_resource_name using the wrapper for unified v1/v2 handling
      let refSpecResourceName = feedSelection?.ref_spec_resource_name || 'feed_spec.object_name';
      let feedParams: any = {};

      if (vendorFeedSpecsWrapper) {
        // For V2 templates, we need to merge data_feed_options params with business logic
        if (vendorFeedSpecsWrapper.isV2Template()) {
          // Get the base feed params from data_feed_options
          const baseFeedParams = vendorFeedSpecsWrapper.getFeedParams(feed);
          feedParams = { ...baseFeedParams };

          // Override with any user-selected values from feedSelection
          if (feedSelection?.feed_selected) {
            // Merge any feed-specific parameters that were set by the user
            Object.keys(feedSelection).forEach((key) => {
              if (key.startsWith('feed_param_')) {
                const paramName = key.replace('feed_param_', '');
                feedParams[paramName] = feedSelection[key];
              }
            });
          }

          // Set the resource name parameter
          if (feedParams?.resource_name_param) {
            refSpecResourceName = feedParams.resource_name_param;
          }
        }
      }

      const feedSpec: DataFeedSpec = {
        type: 'data',
        spec: {
          // Core properties (always present)
          ref_spec: feedSelection?.ref_spec_name || 'base_auto', // Default fallback
          'primary.keys': primaryKeys,
          params: {
            [refSpecResourceName]: feed,
            'ui.feed_display_name': feedSelection?.feed_display_name || feed,
            // Include all feed-specific parameters for V2 templates
            ...(vendorFeedSpecsWrapper?.isV2Template() ? feedParams : {}),
          },
        },
      };

      // Add field information if available
      if (shouldAddFieldInfo && feedSelection?.feed_selected && feedSpec.spec) {
        const selectedFields = Object.keys(feedSelection?.selectedFields || {});
        if (feedSpec.spec?.params) {
          feedSpec.spec.params[feedSelection?.ref_field_list_selector || ''] = useFieldsList;
          feedSpec.spec.params[feedSelection?.ref_fields_names || ''] = useFieldsList
            ? selectedFields.join(',')
            : '';
          feedSpec.spec.params['ui.all_fields_length'] = useFieldsList
            ? feedSelection.allFields?.length || feedSelection.allFieldsLength || 0
            : 0;
          feedSpec.spec.params['ui.selected_fields'] = useFieldsList
            ? selectedFields.reduce((acc, field) => {
                acc[field] =
                  feedSelection?.allFields?.find((f) => f.value === field)?.label || field;
                return acc;
              }, {})
            : {};
        }
      }

      // Set skip_feed based on selection state and object detection mode
      if (feedSpec.spec) {
        // For object detection mode: include all feeds but mark unselected ones as skip_feed: true
        // For non-object detection mode: only include selected feeds (skip_feed: false)
        if (objectDetectionModeEnabled) {
          // Object detection mode: include all feeds, mark unselected as skip_feed: true
          feedSpec.spec.skip_feed = !feedSelection?.feed_selected;
        } else {
          // Non-object detection mode: only include selected feeds
          feedSpec.spec.skip_feed = !feedSelection?.feed_selected;
        }

        // Conditionally add force_resync if supported and set
        if (feedSelection?.force_resync !== undefined) {
          const selectedRefFeed = feedSelection?.ref_spec_name || 'base_auto';
          const supportsForceResync = vendorFeedSpecsWrapper?.supportsForceResync(selectedRefFeed);

          debugLogger.debugLog(2, `Force resync check for feed ${feed}:`, {
            selectedRefFeed,
            supportsForceResync,
            forceResyncValue: feedSelection?.force_resync,
            willInclude: supportsForceResync,
          });

          if (supportsForceResync) {
            feedSpec.spec.force_resync = feedSelection?.force_resync;
          } else {
            // Remove force_resync if the ref_feed doesn't support it
            delete feedSpec.spec.force_resync;
          }
        }
      }

      // Merge with existing backend data if available
      processedFeeds[feed] = mergeFeedWithBackendData(feedSpec, originalFeed);
    });

    // Determine which feeds to include in final payload based on object detection mode
    if (objectDetectionModeEnabled) {
      debugLogger.debugLog(
        2,
        'Object detection mode selector enabled: Including all feeds in final payload with skip_feed flags',
      );
      // Object detection mode: include ALL feeds (both selected and unselected)
      // Selected feeds will have skip_feed: false, unselected feeds will have skip_feed: true
      feeds = processedFeeds;
    } else {
      debugLogger.debugLog(
        2,
        'Object detection mode selector disabled: Only including selected feeds in final payload',
      );
      // Non-object detection mode: only include selected feeds (skip_feed: false)
      Object.keys(processedFeeds).forEach((feed) => {
        if (!processedFeeds[feed]?.spec?.skip_feed) {
          feeds[feed] = processedFeeds[feed];
        }
      });
    }

    debugLogger.debugLog(1, 'Final feeds config:', feeds);
    debugLogger.debugLog(1, 'Total feeds processed:', Object.keys(processedFeeds).length);
    debugLogger.debugLog(1, 'Feeds included in final payload:', Object.keys(feeds).length);

    // Debug skip_feed logic
    debugLogger.debugLog(2, '=== SKIP_FEED DEBUG ===');
    Object.keys(processedFeeds).forEach((feed) => {
      const feedSpec = processedFeeds[feed];
      const feedSelection = selectedFeeds[feed];
      debugLogger.debugLog(2, `Feed ${feed}:`, {
        feed_selected: feedSelection?.feed_selected,
        skip_feed: feedSpec?.spec?.skip_feed,
        included_in_payload: !!feeds[feed],
        objectDetectionModeEnabled,
        feed_params_count: Object.keys(feedSpec?.spec?.params || {}).length,
        feed_params: feedSpec?.spec?.params,
      });
    });
    debugLogger.debugLog(2, '========================');

    debugLogger.debugLog(1, '=== END BUILD SOURCE INFO DEBUG ===');

    // Build the final source structure
    // extract start.cron from params and add it to template_config
    const cron = params['start.cron'];
    delete params['start.cron'];

    // For V2 templates, ensure capability parameters are included in the payload
    let finalParams = { ...params };
    if (vendorFeedSpecsWrapper?.isV2Template()) {
      // Extract capability parameters from sourceConfig and include them with defaults
      Object.values(vendorFeedSpecs?.capabilities || {}).forEach((capability: any) => {
        if (capability.enabled && capability.params) {
          capability.params.forEach((param: any) => {
            if (param.name) {
              // Use user-selected value if available, otherwise use default
              const paramValue =
                sourceConfig[param.name] !== undefined
                  ? sourceConfig[param.name]
                  : param.default || capability.default;

              if (paramValue !== undefined) {
                finalParams[param.name] = paramValue;
              }
            }
          });
        }
      });
    }

    return {
      name,
      description: description || '',
      // Preserve the structured template_config
      template_config: {
        params: finalParams,
        feeds_config: { feeds },
        'start.cron': cron,
      },
      // Include other source properties (including start.cron) as flat properties
      vendor_endpoint_id,
      flow_type: flowType,
    };
  }

  function buildSelectFeeds(source, resourceParams) {
    debugLogger.debugLog(2, 'buildSelectFeeds called with:', { source, resourceParams });
    let refFieldListSelector, refFieldsNames;

    if (resourceParams) {
      forEach(resourceParams, (config) => {
        const { param_type = 'none', ...otherParamProps } = config;
        if (param_type === 'field_list_selector') {
          const param = otherParamProps?.default?.replace(/[{}]/g, '');
          refFieldListSelector = param;
        }
        if (param_type === 'field_list') {
          const param = otherParamProps?.default?.replace(/[{}]/g, '');
          refFieldsNames = param;
        }
      });
    }

    if (source.template_config?.params) {
      Object.keys(source.template_config.params).forEach((key) => {
        if (key.includes('use_fields_list')) {
          const value = source.template_config.params?.[key]?.replace(/[{}]/g, '') || '';
          refFieldListSelector = value;
        }
        if (key.includes('fields_list')) {
          const value = source.template_config.params?.[key]?.replace(/[{}]/g, '') || '';
          refFieldsNames = value;
        }
      });
    }

    // Safety check for feeds_config.feeds
    if (!source.template_config?.feeds_config?.feeds) {
      console.warn('buildSelectFeeds: No feeds found in source template config');
      return {} as SelectedFeedsType;
    }

    debugLogger.debugLog(
      2,
      'buildSelectFeeds: Processing feeds:',
      source.template_config.feeds_config.feeds,
    );

    return mapValues(
      source.template_config.feeds_config.feeds,
      (feedSpec: DataFeedSpec, feed: string): FeedSetting => {
        if (feedSpec.type === 'data' && feedSpec.spec) {
          /*

          Logic to convert downloaded source template config to the common feedParams object that UI can understand

          Sample Feed Spec from API:

          feedSpec = {
            type: 'data',
            spec: {
              ref_spec: feedSelection?.ref_spec_name || '',
              'primary.keys': primaryKeys,
              params: {
                [feedSelection?.ref_spec_resource_name || '']: feed,
                                  'ui.feed_display_name': feedSelection?.feed_display_name || feed
              }
          }
          */

          // pick keys from params whose value is feed
          const all_ref_spec_ressource_names = Object.keys(feedSpec.spec?.params || {}).filter(
            (key) => feedSpec.spec?.params?.[key] === feed,
          );

          const ref_spec_resource_name =
            all_ref_spec_ressource_names.length > 1
              ? all_ref_spec_ressource_names.filter((key) => key.startsWith('feed_spec'))[0]
              : all_ref_spec_ressource_names[0];

          return {
            feed_selected: !feedSpec.spec?.skip_feed, // true if skip_feed is false/undefined, false if skip_feed is true
            feed_display_name: feedSpec.spec?.params?.['ui.feed_display_name'] || feed,
            feed_type: feedSpec.spec?.ref_spec ? 'ref_feed' : 'base_feed',
            allFieldsLength: feedSpec.spec?.params?.['ui.all_fields_length'] || undefined,
            selectedFields: feedSpec.spec?.params?.['ui.selected_fields'] || {},
            ref_field_list_selector:
              feedSpec.spec?.params?.['ui.ref_field_list_selector'] || refFieldListSelector,
            ref_fields_names: feedSpec.spec?.params?.['ui.ref_fields_names'] || refFieldsNames,
            ref_spec_name: feedSpec.spec?.ref_spec || 'base_auto',
            primary_keys: feedSpec.spec['primary.keys']?.join(',') || '',
            ref_spec_resource_name: ref_spec_resource_name || '', //get the key for first object in param whose value is

            // Known backend properties - load from existing source for editing
            // Black box properties (not editable by UI) - only add if they exist
            ...(feedSpec.last_ingested !== undefined && { last_ingested: feedSpec.last_ingested }),
            ...(feedSpec.detected !== undefined && { detected: feedSpec.detected }),

            // User-editable properties - only add if they exist (backward compatibility)
            ...(feedSpec.spec?.force_resync !== undefined && {
              force_resync: feedSpec.spec.force_resync,
            }),
            ...(feedSpec.spec?.skip_feed !== undefined && { skip_feed: feedSpec.spec.skip_feed }),
          };
        } else {
          // Handle cases where feedSpec.type !== 'data' or feedSpec.spec is missing
          console.warn(
            `buildSelectFeeds: Unexpected feed spec structure for feed ${feed}:`,
            feedSpec,
          );
          return {
            feed_selected: false,
            feed_display_name: feed,
            feed_type: 'base_feed',
            selectedFields: {},
            allFieldsLength: 0,
            ref_spec_name: '',
            primary_keys: '',
            ref_spec_resource_name: '',
          };
        }
      },
    ) as SelectedFeedsType;
  }

  return {
    canEditPrefix,
    getBucketInfoFromConfig,
    getMissingFields,
    buildSourceInfo,
    buildSelectFeeds,
  };
}
